/**
 * Strategy → Tracking — global entry for rank + Search Console measurement.
 * Same panel as the old project tab; project picker matches Calendars / Campaigns.
 *
 * The selected project lives in the URL (`?project=`) as the single source
 * of truth — shareable, Back/Forward-friendly. localStorage only seeds a
 * default on first visit; it never fights the URL (the old two-way sync
 * effect rewrote the param over history entries and trapped the Back button).
 */
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Radar } from 'lucide-react';
import { Page, PageHeader } from '@/components/ui/page';
import { Section } from '@/components/ui/section';
import { EmptyState } from '@/components/ui/empty';
import { FormField } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/spinner';
import { useProjects } from '@/features/projects/use-projects';
import { TrackingPanel } from '@/features/ranks/components/tracking-panel';
import { hostname } from '@/lib/format';

const PROJECT_FILTER_STORAGE = 'seo:tracking-project';

export function TrackingRoute(): JSX.Element {
  const projects = useProjects();
  const availableProjects = projects.data?.items ?? [];
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { project?: string };

  const projectId = search.project ?? '';

  const setProject = (next: string): void => {
    try {
      if (next) window.localStorage.setItem(PROJECT_FILTER_STORAGE, next);
      else window.localStorage.removeItem(PROJECT_FILTER_STORAGE);
    } catch {
      /* ignore */
    }
    void navigate({
      to: '/tracking',
      // Explicit object — never spread prev, so clearing actually clears.
      search: next ? { project: next } : {},
      replace: true,
    });
  };

  // First-visit default only: seed from localStorage (or the sole project)
  // exactly once per mount. Never re-runs on later URL changes, so Back to a
  // bare /tracking stays bare.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (projects.isLoading) return;
    seededRef.current = true;
    if (search.project) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(PROJECT_FILTER_STORAGE);
    } catch {
      /* ignore */
    }
    const candidate =
      (stored && availableProjects.some((p) => p.id === stored) ? stored : null) ??
      (availableProjects.length === 1 ? availableProjects[0]!.id : null);
    if (candidate) {
      void navigate({
        to: '/tracking',
        search: { project: candidate },
        replace: true,
      });
    }
  }, [projects.isLoading, availableProjects, search.project, navigate]);

  const activeProject = availableProjects.find((p) => p.id === projectId);

  return (
    <Page>
      <PageHeader
        title="Tracking"
        description="Watch keyword positions and Search Console movement — so you can tell whether briefs, fixes, and content actually moved the needle."
      />

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <Section>
          <FormField label="Project">
            {availableProjects.length === 0 && !projects.isLoading ? (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>No projects yet.</p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/projects">Create one</Link>
                </Button>
              </div>
            ) : (
              <Select value={projectId || undefined} onValueChange={setProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a project" />
                </SelectTrigger>
                <SelectContent>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          {activeProject ? (
            <div className="mt-2 text-xs text-muted-foreground">
              {hostname(activeProject.domain)}
            </div>
          ) : null}
        </Section>

        <div className="min-w-0">
          {projects.isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : !projectId || !activeProject ? (
            <EmptyState
              icon={Radar}
              title="Pick a project to track"
              description="Tracking is per site — choose which project’s keywords and Search Console data to watch using the picker on the left."
              variant="inline"
            />
          ) : (
            <TrackingPanel
              projectId={projectId}
              project={activeProject}
              hideIntro
            />
          )}
        </div>
      </div>
    </Page>
  );
}
